import React, {useCallback, useEffect, useState} from 'react';
import {
  Modal,
  customStyles,
  DropdownIndicator,
  PrimaryButton,
  Checkbox,
} from '../../../../components';
import {closeModal, uploadCsv} from '../../../../assets/images/images';
import Select from 'react-select';
import {toast} from 'react-toastify';
import useDragAndDrop from '../../../../hooks/useDragAndDrop';

import {accountTransactionType} from '../Recon_Constants';
import {useMutation, useQueryClient} from 'react-query';
import {
  addTransaction,
  uploadEncryptedFile,
  uploadTransaction,
} from '../../../../requests/queries/reconciliations';
import {useAccounts} from '../hooks/useAccounts';
import {GoBack} from '../components/GoBack';
import {useParams} from 'react-router-dom';
import DocumentMatching from './DocumentMatching';
import {createFormData, createTrimmedFile} from '../constants';

const initialInputs = {
  amount: '',
  ref: '',
  time: '',
};
function ManualTransactionModal({
  show,
  onClose,
  goBack,
  noUpload,
  selectedAccount,
  setCanStart,
}) {
  const {slug} = useParams();
  const {accountSelectionData} = useAccounts({slug});
  const [file, setFileData] = useState(null);
  const [keys, setKeys] = useState(null);
  const [inputs, setInputs] = useState(initialInputs);
  const [account, setAccount] = useState(null);
  const [matchScreen, setMatchScreen] = useState(false);
  const [isPassword, setIsPassword] = useState(false);
  const [documentPassword, setDocumentPassword] = useState('');
  const [encryptedFileData, setEncryptedFileData] = useState({});

  const [transactionType, setTransactionType] = useState(
    accountTransactionType[0],
  );

  const queryClient = useQueryClient();
  const {dragOver, setDragOver, onDragOver, onDragLeave} = useDragAndDrop();

  useEffect(() => {
    setAccount(
      accountSelectionData?.length > 0
        ? accountSelectionData[0]
        : selectedAccount,
    );
    return () => setMatchScreen(false);
  }, [accountSelectionData, selectedAccount]);
  useEffect(() => setMatchScreen(false), [setMatchScreen]);

  const {mutate, isLoading} = useMutation(
    'add-account-transaction',
    addTransaction,
  );

  const {mutate: uploadData, isLoading: uploadLoading} = useMutation(
    'upload-account-transaction',
    uploadTransaction,
  );

  const encryptedFileUploadMutation = useMutation(uploadEncryptedFile);

  const handleFile = file => {
    if (file) {
      setFileData(file);
    }
  };

  const onDrop = e => {
    e.preventDefault();
    setDragOver(false);
    const selectedFile = e?.dataTransfer?.files[0];
    const uploadType = ['xlsx', 'csv', 'xls', 'pdf'];
    const fileExt = selectedFile.name.split('.');
    if (!uploadType.includes(fileExt[fileExt.length - 1])) {
      return toast.error(
        `Please provide a valid file type - (xlsx, csv, xls, pdf)`,
      );
    }
    handleFile(selectedFile);
  };

  const handleInputs = e => {
    const {name, value} = e.target;
    setInputs(prev => ({...prev, [name]: value}));
  };

  const handleUploadSuccess = useCallback(() => {
    queryClient.invalidateQueries();
    onClose(false);
    setMatchScreen(prev => !prev);
    setKeys(null);
    setFileData(null);
    setEncryptedFileData({});
    setIsPassword(false);
    setCanStart(true);
    setDocumentPassword('');
    sessionStorage.setItem('tabType', 'internal');
  }, [onClose, queryClient, setCanStart]);

  const handleEncryptedFile = async () => {
    if (!file || !isPassword || !documentPassword) {
      toast.error(
        'Please provide all required information for encrypted file upload.',
      );
      return;
    }

    if (!account?.value) {
      toast.error('Please select an account.');
      return;
    }

    const trimmedFile = createTrimmedFile(file);
    const formData = createFormData({
      file: trimmedFile,
      password: String(documentPassword),
    });

    try {
      await encryptedFileUploadMutation.mutate(formData, {
        onSuccess: data => {
          setEncryptedFileData(data?.entity);
          setMatchScreen(!matchScreen);
        },
        onError: error => {
          toast.error(error.response.data.error);
        },
      });
    } catch (error) {
      toast.error(error.response.data.error);
    }
  };

  const handleSubmit = async documentKeys => {
    if (file && account?.value) {
      if (!account?.value) {
        return toast.error('Please select an account.');
      }

      const isPasswordEmpty = documentPassword?.length === 0;

      const trimmedFile = createTrimmedFile(file);
      const formData = createFormData({
        file: isPasswordEmpty ? trimmedFile : null,
        request_id: !isPasswordEmpty ? encryptedFileData?.request_id : null,
        account_id: account?.value || selectedAccount?.value,
        currency: account?.currency || selectedAccount?.currency,
        keys: JSON.stringify(documentKeys),
      });

      const passwordData = createFormData({
        request_id: encryptedFileData?.request_id,
        account_id: account?.value || selectedAccount?.value,
        keys: JSON.stringify(documentKeys),
      });

      const finalData = isPasswordEmpty ? formData : passwordData;
      try {
        await uploadData(finalData, {
          onSuccess: () => {
            toast.success('File uploaded successfully');
            handleUploadSuccess();
          },
          onError: error => {
            toast.error(error.response.data?.error);
          },
        });
      } catch (e) {
        console.log(e);
      }
      return;
    }
    try {
      const truthyData = Object.fromEntries(
        Object.entries(inputs).filter(([, value]) => value),
      );
      const data = {
        account_id:
          account && account?.value?.length > 0
            ? account?.value
            : selectedAccount?.value,
        type: 'bank_transaction',
        event: {
          transaction: {
            ...truthyData,
            type: transactionType?.value,
          },
        },
      };

      if (data) {
        mutate(data, {
          onSuccess: () => {
            toast.success('Transaction added successfully');
            queryClient.invalidateQueries();
            onClose(false);
            setInputs(initialInputs);
          },
          onError: error => {
            toast.error(error.response.data.error);
          },
        });
      }
    } catch (error) {
      toast.error(error.response.data.error);
    }
  };

  const isMatchingPage =
    (file || encryptedFileData?.headers?.length) && matchScreen;

  return (
    <Modal
      show={show}
      onClose={() => {
        onClose(false);
        setMatchScreen(false);
        setFileData(null);
      }}
      extraModalClasses={`${
        isMatchingPage ? 'max-w-[580px]' : 'max-w-[480px]'
      } overflow-y-auto h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg absolute bottom-0 sm:relative lookup-modal`}
      modalPosition="justify-center sm:justify-end"
    >
      <div className="px-6 pt-6 pb-12">
        <div className="flex items-center justify-between w-full">
          {goBack && (
            <GoBack
              goBack={() => {
                if (matchScreen) {
                  return setMatchScreen(!matchScreen);
                }
                goBack();
                onClose(false);
              }}
            />
          )}

          <button
            onClick={() => {
              onClose(false);
              setFileData(null);
              handleUploadSuccess();
            }}
            className="hidden sm:block ml-auto"
          >
            <img src={closeModal} alt="" />
          </button>
        </div>

        {isMatchingPage ? (
          <DocumentMatching
            file={file}
            setKeys={setKeys}
            isDisabled={!file && !keys && (!account || !selectedAccount?.value)}
            handleSubmit={handleSubmit}
            loading={uploadLoading}
            encryptedHeaders={encryptedFileData?.headers}
          />
        ) : (
          <div className="relative">
            <h3
              className="font-medium text-left mt-5 text-base text-grey"
              id="addManualTransaction"
            >
              Create Manual Transaction
            </h3>

            <label
              htmlFor="ID_type"
              className="relative mt-6 text-body font-medium "
            >
              <span className="required">Select account</span>
            </label>
            <Select
              defaultValue={selectedAccount || account}
              placeholder="Select an account"
              classNamePrefix="react-select"
              components={{DropdownIndicator}}
              styles={customStyles}
              className="text-start mt-2 add_project_select"
              menuPlacement="auto"
              options={accountSelectionData}
              onChange={setAccount}
              isDisabled={!!selectedAccount}
            />
            <label htmlFor="amount" className="mt-6">
              <span className="text-sm text-body font-medium required">
                Amount
              </span>
              <input
                type="number"
                name="amount"
                className="mt-2 placeholder:text-grey-200 placeholder:font-normal"
                id="amount"
                placeholder="Enter amount"
                value={inputs?.amount}
                onChange={handleInputs}
              />
            </label>

            {!selectedAccount && (
              <label htmlFor="reference_id" className="mt-6">
                <span className="text-sm text-body font-medium required">
                  Reference ID
                </span>
                <input
                  type="text"
                  name="ref"
                  className="mt-2 placeholder:text-grey-200 placeholder:font-normal"
                  id="ref"
                  placeholder="Input reference ID"
                  value={inputs?.ref}
                  onChange={handleInputs}
                />
              </label>
            )}

            <label htmlFor="date" className="mt-6">
              <span className="text-sm text-body font-medium required">
                Date
              </span>
              <input
                type="date"
                name="time"
                className="mt-2 placeholder:text-grey-200 placeholder:font-normal"
                id="time"
                placeholder="Date"
                value={inputs?.time}
                onChange={handleInputs}
              />
            </label>

            <label
              htmlFor="ID_type"
              className="relative mt-6 text-body font-medium"
            >
              Type
            </label>
            <Select
              defaultValue={transactionType}
              placeholder="Select an ID type"
              classNamePrefix="react-select"
              components={{DropdownIndicator}}
              styles={customStyles}
              className="text-start mt-2 add_project_select"
              menuPlacement="auto"
              options={accountTransactionType}
              onChange={setTransactionType}
            />

            {!noUpload && (
              <div className="flex items-center justify-center w-full mt-10 mb-6">
                <h1 className="text-grey font-medium text-lg">Or</h1>
              </div>
            )}
            {!noUpload && (
              <>
                <div
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  style={{
                    border: `${dragOver ? '3px dashed #33333322' : ''}`,
                  }}
                  className="py-[22px] flex flex-col items-center gap-[2px] text-center border border-brandBlue border-dashed rounded"
                >
                  <div className="flex-col items-center hidden sm:flex">
                    {!file && (
                      <img src={uploadCsv} alt="" width={48} height={48} />
                    )}
                    <p className="text-body">
                      {dragOver ? (
                        'Please drop file here'
                      ) : (
                        <span style={{color: file ? '#00000032' : ''}}>
                          Drag and drop a{' '}
                          <span className="text-grey40 italic">
                            (.csv, xsl or .xslx)
                          </span>{' '}
                          file here
                        </span>
                      )}
                    </p>
                  </div>
                  {file ? (
                    <div className="text-body text-sm flex my-2 gap-[3px] items-baseline">
                      <input
                        type="file"
                        id="file"
                        onChange={e => {
                          handleFile(e.target.files[0]);
                        }}
                        accept=".xlsx, .csv, .xls"
                      />
                      <label htmlFor="file">Change file</label>
                    </div>
                  ) : (
                    <div className="text-body text-sm flex gap-[3px] items-baseline">
                      <span className="hidden sm:block">or</span>
                      <input
                        type="file"
                        id="file"
                        onChange={e => {
                          handleFile(e.target.files[0]);
                        }}
                        accept=".xlsx, .csv, .xls"
                      />
                      <label htmlFor="file">click here to select</label>
                      from your computer
                    </div>
                  )}
                  <p className="text-body text-sm flex gap-[3px] items-baseline">
                    {file ? (
                      <>
                        File:{' '}
                        <span className="font-bold text-black ">
                          {file.name}
                        </span>
                      </>
                    ) : (
                      ''
                    )}
                  </p>
                </div>
              </>
            )}

            {file && (
              <div className="mt-3 flex items-center mb-3">
                <Checkbox
                  label={`Is there password for this document?`}
                  className="py-[10px]"
                  labelStyle="text-sm text-body leading-[18px]"
                  noLeftPadding
                  isChecked={isPassword}
                  onChange={e => {
                    setIsPassword(e.target.checked);
                    setDocumentPassword('');
                  }}
                />
                <span className="text-grey40 text-sm pl-1"> (Optional)</span>
              </div>
            )}
            {isPassword && file && (
              <>
                <label htmlFor="password " className="mb-2">
                  Enter Document Password
                </label>
                <input
                  type="password"
                  value={documentPassword}
                  onChange={e => setDocumentPassword(e.target.value)}
                />
              </>
            )}

            <div className="flex items-center mt-10">
              {file && !matchScreen ? (
                <PrimaryButton
                  onClick={() =>
                    documentPassword?.length > 0
                      ? handleEncryptedFile()
                      : setMatchScreen(!matchScreen)
                  }
                  buttonText="Next"
                  className="w-full rounded-lg"
                  disabled={
                    !file || (isPassword && documentPassword?.length === 0)
                  }
                  loading={encryptedFileUploadMutation?.isLoading}
                />
              ) : (
                <PrimaryButton
                  onClick={handleSubmit}
                  buttonText="Submit"
                  className="w-full rounded-lg"
                  loading={isLoading || uploadLoading}
                  disabled={
                    selectedAccount
                      ? !file && (!inputs.amount || !inputs.time)
                      : (Object.values(inputs || {}).some(e => !e) ||
                          !transactionType?.value) &&
                        (!file || !account?.value)
                  }
                />
              )}
            </div>
            <div className="flex items-center mt-6"></div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ManualTransactionModal;
